import { createLearnerPlanApi } from "@/api/services/main/learner";
import { useEnum } from "@/api/services/global/enum";
import { TIndustryExam, TIndustrySubject } from "@/api/services/main/company/dtos/get-company-list.dto";
import { Button } from "@/components/elements/Button";
import { CheckboxGroup, TCheckboxOption } from "@/components/elements/CheckboxGroup";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { InputDatePicker } from "@/components/elements/InputDatePicker";
import { Select } from "@/components/elements/Select";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { CurriculumScheduleTable } from "@/features/learner/components/CurriculumScheduleTable";
import { learnerPlanSchema } from "@/features/learner/schema/learnerSchema";
import { TLearnerPlan } from "@/features/learner/types/learnerForm";
import { calculateAvailableLearningWeeks, getNextOrCurrentMonday } from "@/features/learner/utils/calculates";
import { convertLearnerPlanFormDataReq } from "@/features/learner/utils/convertLearnerFormData";
import { UseVisibleReturn } from "@/hooks/useVisible";
import { background, black, divider, state, text } from "@/theme/colors";
import { FCC, TOption } from "@/types/common";
import { CURRICULUM_PROGRESS, EXAM_METHOD, EXAM_STATUS } from "@/types/enum";
import { dayjs } from "@/utils/dayjs";
import { showError } from "@/utils/error";
import { toast } from "@/utils/toast";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Grid, Modal, Stack, styled, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { IconClose, IconPlus, IconWarning } from "@/assets/icons";
import { IconBox } from "@/components/elements/IconBox";

import { MOCK_EXAM_WEEKS } from "@/constants/common";

type TCreateLearnerPlanModalProps = {
  modal: UseVisibleReturn;
  learnerId: string;
  industryPartTypes: string[];
  industryExams: TIndustryExam[];
  defaultValues?: Partial<TLearnerPlan>;
  subjects: TIndustrySubject[];
  mockExamQuestionCount?: number | null;
};

export const CreateLearnerPlanModal: FCC<TCreateLearnerPlanModalProps> = ({
  modal, learnerId, industryPartTypes, industryExams, defaultValues, subjects, mockExamQuestionCount,
}) => {
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { data: planStatusOptions } = useEnum("EPlanStatus");
  const { data: curriculumProgressOptions } = useEnum("ECurriculumProgress");
  const { data: allPartTypeOptions } = useEnum("EPartType");

  const defaultCurriculumProgressStatus =
    defaultValues?.curriculumProgressStatus || (curriculumProgressOptions?.length ? curriculumProgressOptions[0].value : undefined);
  const formDefaults = useMemo<Partial<TLearnerPlan>>(
    () => ({
      ...defaultValues,
      partTypes: defaultValues?.partTypes ?? (industryPartTypes.length > 0 ? industryPartTypes : null),
      curriculumProgressStatus: defaultCurriculumProgressStatus,
    }),
    [defaultValues, industryPartTypes, defaultCurriculumProgressStatus],
  );

  const methods = useForm<TLearnerPlan>({
    defaultValues: formDefaults,
    resolver: yupResolver(learnerPlanSchema),
    mode: "all",
    reValidateMode: "onBlur",
  });

  const { control, reset, setValue } = methods;

  // Reset form only on modal open transition (false → true), not on formDefaults ref changes
  const prevVisibleRef = useRef(modal.visible);
  useEffect(() => {
    if (modal.visible && !prevVisibleRef.current) {
      reset(formDefaults);
    }
    prevVisibleRef.current = modal.visible;
  }, [modal.visible, formDefaults, reset]);

  const learningStartDate = useWatch({ control, name: "learningStartDate" });
  const examScheduledYearMonth = useWatch({ control, name: "examScheduledYearMonth" });
  const examId = useWatch({ control, name: "examId" });
  const selectedPartTypes = useWatch({ control, name: "partTypes" });
  const curriculumProgressStatus = useWatch({ control, name: "curriculumProgressStatus" });

  // partTypes null → show all subjects; partTypes [] with values → filter by selected
  const filteredSubjects = useMemo(() => {
    if (!subjects.length) return [];
    if (!selectedPartTypes) return subjects;
    return subjects.filter((s) => selectedPartTypes.includes(s.partType));
  }, [subjects, selectedPartTypes]);

  // Build partType checkbox options
  const hasPartTypes = industryPartTypes.length > 0;
  const partTypeOptions: TCheckboxOption[] = useMemo(
    () => allPartTypeOptions
      .filter((p) => industryPartTypes.includes(String(p.value)))
      .map((p) => ({ value: p.value, label: p.label + "パート" })),
    [allPartTypeOptions, industryPartTypes],
  );

  // Build exam select options
  const examOptions: TOption[] = useMemo(
    () => industryExams
      .filter((e) => e.status === EXAM_STATUS.ACTIVE)
      .map((e) => ({ value: e.id.toString(), label: e.periodName })),
    [industryExams],
  );

  // Lock examScheduledYearMonth when selected exam is PAPER_TEST
  const selectedExam = useMemo(
    () => industryExams.find((e) => String(e.id) === String(examId)),
    [industryExams, examId],
  );
  const isPaperTest = selectedExam?.examMethod === EXAM_METHOD.PAPER_TEST;

  const prevExamId = useRef(examId);
  useEffect(() => {
    if (isPaperTest && selectedExam?.examMonth) {
      const isUserChange = prevExamId.current !== examId;
      setValue("examScheduledYearMonth", dayjs(selectedExam.examMonth).startOf("month"), { shouldDirty: isUserChange });
    }
    prevExamId.current = examId;
  }, [isPaperTest, selectedExam, examId, setValue]);

  const localSubjectCount = useMemo(() => {
    if (!subjects.length) return null;
    return filteredSubjects.length;
  }, [subjects, filteredSubjects]);

  const availableWeeks = calculateAvailableLearningWeeks(learningStartDate, examScheduledYearMonth);
  const isWeeksInsufficient = availableWeeks !== null && localSubjectCount !== null && (localSubjectCount + MOCK_EXAM_WEEKS) > availableWeeks;

  useEffect(() => {
    if (curriculumProgressStatus === CURRICULUM_PROGRESS.KEEP_PREVIOUS && defaultValues?.learningStartDate) {
      setValue("learningStartDate", defaultValues.learningStartDate, {
        shouldValidate: true,
        shouldDirty: true,
      });
    } else if (curriculumProgressStatus === CURRICULUM_PROGRESS.RESET) {
      setValue("learningStartDate", getNextOrCurrentMonday(), {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [curriculumProgressStatus, setValue, defaultValues?.learningStartDate]);

  const onClose = () => {
    setIsSubmitting(false);
    reset(formDefaults);
    modal.close();
  };

  const onSubmit = async (data: TLearnerPlan) => {
    try {
      setIsSubmitting(true);
      await createLearnerPlanApi(learnerId, convertLearnerPlanFormDataReq(data));
      toast.success(API_SUCCESS_MESSAGE);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.LEARNER.FETCH_DETAIL, learnerId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.LEARNER.FETCH_PLAN_LIST, learnerId] });
      reset(formDefaults);
      modal.close();
    } catch (e) {
      showError(e);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal open={modal.visible} onClose={onClose} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
      <ModalContainer>
        <Form methods={methods} onSubmit={onSubmit} schema={learnerPlanSchema} enableBlocker={false}>
          <Stack
            bgcolor={background.disable}
            height="56px"
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            pl={3}
            pr={3}
            flexShrink={0}
            sx={{ borderTopLeftRadius: "8px", borderTopRightRadius: "8px" }}
          >
            <Typography variant="body14Light" color={text.primary}>
              新しい受講カリキュラムを計画
            </Typography>
            <IconBox onClick={onClose} sx={{ p: 0 }}>
              <IconClose color={black[80]} fontSize={24} />
            </IconBox>
          </Stack>

          {/* Scrollable content */}
          <Stack p={4} gap={3} sx={{ overflowY: "auto", flex: 1 }}>
            <Typography variant="body14" color={text.secondary}>
              新しい受講カリキュラムを立てます。以前のカリキュラムは停止され、新しいスケジュールが組まれます。
            </Typography>
            <Grid container rowSpacing={2} columnSpacing={3}>
              <Grid item xs={6}>
                <FormField control={control} name="planStatus" label="受験ステータス">
                  <Select options={planStatusOptions} />
                </FormField>
              </Grid>
              <Grid item xs={6}>
                <FormField control={control} name="curriculumProgressStatus" label="カリキュラムの進行状態">
                  <Select options={curriculumProgressOptions} />
                </FormField>
              </Grid>
              <Grid item xs={6}>
                <FormField control={control} name="learningStartDate" label="カリキュラム開始日" required>
                  <InputDatePicker placeholder="2022/01/01" />
                </FormField>
              </Grid>
              <Grid item xs={6}>
                {hasPartTypes && (
                  <FormField control={control} name="partTypes" label="受験・学習対象のパート" required>
                    <CheckboxGroup options={partTypeOptions} flexDirection="row" />
                  </FormField>
                )}
              </Grid>
              <Grid item xs={6}>
                <FormField control={control} name="examId" label="受験予定" required>
                  <Select options={examOptions} placeholder="選択してください" />
                </FormField>
              </Grid>
              <Grid item xs={6}>
                <FormField control={control} name="examScheduledYearMonth" label="受験予定年月" required>
                  <InputDatePicker
                    views={["year", "month"]}
                    openTo="month"
                    format="YYYY/MM"
                    placeholder="2027/01"
                    disabled={isPaperTest}
                  />
                </FormField>
              </Grid>
              <Grid item xs={6}>
                <Stack gap={1}>
                  <Typography variant="cap12" color={text.secondary}>
                    大まかな学習可能な期間（週数）
                  </Typography>
                  <Typography variant="body14Semi" color={text.primary}>
                    {availableWeeks !== null ? `${availableWeeks}週間` : "-"}
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={6}>
                <Stack gap={1}>
                  <Typography variant="cap12" color={text.secondary}>
                    学習に必要な期間（週数）
                  </Typography>
                  <Typography variant="body14Semi" color={text.primary}>
                    {localSubjectCount !== null ? `${localSubjectCount + MOCK_EXAM_WEEKS}週間（${localSubjectCount} WEEK + 模擬試験）` : "-"}
                  </Typography>
                </Stack>
              </Grid>
              {isWeeksInsufficient && (
                <Grid item xs={12}>
                  <Stack direction="row" alignItems="center" gap={0.5}>
                    <IconWarning />
                    <Typography variant="cap12" color={state.warning_1}>
                      受講可能な週数が科目数より少ないため、学習期間が不足する可能性があります。
                    </Typography>
                  </Stack>
                </Grid>
              )}
              {learningStartDate && (
                <Grid item xs={12}>
                  <CurriculumScheduleTable
                    learningStartDate={learningStartDate}
                    subjects={filteredSubjects}
                    mockExamQuestionCount={mockExamQuestionCount}
                    heightTable="calc(100vh - 564px)"
                  />
                </Grid>
              )}
              {/* TODO: 自習モード - temporarily hidden
              <Grid item xs={12}>
                <FormField control={control} name="isSelfStudyMode" label="">
                  <Checkbox label="自己学習モード" checked={!!isSelfStudyMode} />
                  <Typography variant="cap12" color={text.secondary} ml={3.5}>
                    学習者が自ら科目を選択して自立的に学習を進めるモードです。
                  </Typography>
                </FormField>
              </Grid>
              */}
            </Grid>
          </Stack>

          {/* Footer - fixed */}
          <Stack direction="row" justifyContent="space-between" p="16px 32px" flexShrink={0} sx={{ borderTop: `1px solid ${divider.middle}` }}>
            <Button variant="ghost" onClick={onClose} size="sm">
              キャンセル
            </Button>
            <Button variant="primary" type="submit" size="sm" isLoading={isSubmitting} startIcon={<IconPlus />}>
              計画を設定する
            </Button>
          </Stack>
        </Form>
      </ModalContainer>
    </Modal>
  );
};

const ModalContainer = styled(Box)`
  width: 920px;
  background-color: ${background.white};
  box-shadow: 0px 8px 24px 0px #00000026;
  border-radius: 8px;
  max-height: calc(100vh - 38px);
  display: flex;
  flex-direction: column;
  outline: none;
  &:focus {
    outline: none;
  }
  & > form {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    max-height: inherit;
  }
`;
